import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

/* Background prefetch scheduler — limits how many product pages are fetched at once
   so warming the quick-add cache doesn't hammer the network on collection pages. */
const QUICK_ADD_MAX_CONCURRENT = 3;
let quickAddActive = 0;
/** @type {Array<() => Promise<unknown>>} */
const quickAddQueue = [];
function quickAddDrain() {
  while (quickAddActive < QUICK_ADD_MAX_CONCURRENT && quickAddQueue.length) {
    const task = quickAddQueue.shift();
    if (!task) break;
    quickAddActive++;
    Promise.resolve()
      .then(task)
      .catch(() => {})
      .finally(() => {
        quickAddActive--;
        quickAddDrain();
      });
  }
}
/** @param {() => Promise<unknown>} task */
function quickAddSchedule(task) {
  quickAddQueue.push(task);
  quickAddDrain();
}

/* Shared observer: warm the cache for cards as they approach the viewport, during idle
   time, so the FIRST click on a card's "Add" opens the modal instantly. */
const quickAddObserver =
  typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = /** @type {QuickAddComponent} */ (entry.target);
            obs.unobserve(el);
            const run = () => quickAddSchedule(() => el.prefetchContent());
            if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 2500 });
            else setTimeout(run, 250);
          }
        },
        { rootMargin: '300px' }
      )
    : null;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} in-flight fetches, so click + prefetch share one request */
  #inflight = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);

    // Warm the cache on intent (hover / focus / touch) so the click is instant.
    this.addEventListener('pointerenter', this.prefetchContent);
    this.addEventListener('focusin', this.prefetchContent);
    this.addEventListener('touchstart', this.prefetchContent, { passive: true });

    // And warm it in the background once the card nears the viewport.
    quickAddObserver?.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerenter', this.prefetchContent);
    this.removeEventListener('focusin', this.prefetchContent);
    this.removeEventListener('touchstart', this.prefetchContent);
    quickAddObserver?.unobserve(this);
    this.#abortController?.abort();
  }

  /**
   * Loads the product-grid content for a URL: returns the cached element, an in-flight
   * fetch, or starts a new one. Never fetches the same URL twice concurrently.
   * @param {string} url
   * @returns {Promise<Element | null>}
   */
  #loadContent(url) {
    if (!url) return Promise.resolve(null);
    const cached = this.#cachedContent.get(url);
    if (cached) return Promise.resolve(cached);
    const existing = this.#inflight.get(url);
    if (existing) return existing;

    const promise = this.fetchProductPage(url)
      .then((html) => {
        const gridElement = html?.querySelector('[data-product-grid-content]');
        const grid = gridElement ? /** @type {Element} */ (gridElement.cloneNode(true)) : null;
        if (grid) {
          this.#warmImages(grid);
          this.#cachedContent.set(url, grid);
        }
        this.#inflight.delete(url);
        return grid;
      })
      .catch(() => {
        this.#inflight.delete(url);
        return null;
      });

    this.#inflight.set(url, promise);
    return promise;
  }

  /**
   * Preloads the first product image(s) into the browser cache and marks them eager, so
   * the images are already downloaded (or downloading) by the time the modal opens.
   * @param {Element} grid
   */
  #warmImages(grid) {
    const imgs = grid.querySelectorAll('img');
    for (let i = 0; i < imgs.length && i < 2; i++) {
      const img = imgs[i];
      // Load immediately (not lazily) once inserted into the modal, at high priority.
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'high');
      // Warm the browser cache now, matching srcset/sizes so the same candidate is reused.
      const pre = new Image();
      const sizes = img.getAttribute('sizes');
      const srcset = img.getAttribute('srcset');
      if (sizes) pre.sizes = sizes;
      if (srcset) pre.srcset = srcset;
      const src = img.getAttribute('src');
      if (src) pre.src = src;
    }
  }

  /** Fire-and-forget cache warm for this card's product page. */
  prefetchContent = () => {
    this.#loadContent(this.productPageUrl);
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Reuses the cache / in-flight prefetch warmed on hover or when the card scrolled
    // into view, so this resolves instantly instead of doing a cold fetch on click.
    const productGrid = await this.#loadContent(this.productPageUrl);

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }

      // QMZ: keep the sample size-set checkboxes in the mobile modal, placed BELOW the
      // variant picker (Order Type) and ABOVE the add-to-cart form. They live inside
      // .product-details (which we strip below), so move the block out first.
      const qmzSizeSet = productDetails?.querySelector('[data-qmz-size-set]');
      if (qmzSizeSet) {
        productGrid.appendChild(qmzSizeSet);
      }

      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #origCloseDialog = null;
  #allowClose = false;

  connectedCallback() {
    super.connectedCallback();

    // B2B workflow: keep the quick-add modal open until the user explicitly closes
    // it via the "Shop More" button. Clients order one product in many sizes/quantities,
    // so the modal must not disappear on each add-to-cart, backdrop click, or Escape.
    if (!this.#origCloseDialog) {
      this.#origCloseDialog = this.closeDialog;
      this.closeDialog = this.#guardedCloseDialog;

      // Block native Escape-to-close unless an explicit close is allowed.
      this.refs.dialog?.addEventListener('cancel', (event) => {
        if (!this.#allowClose) event.preventDefault();
      });
    }

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /** Guarded close: ignores backdrop clicks, Escape, and add-to-cart auto-close. */
  #guardedCloseDialog = async (...args) => {
    if (!this.#allowClose) return;
    this.#allowClose = false;
    return this.#origCloseDialog?.(...args);
  };

  /** Closes the modal from the explicit "Shop More" / "✕" button. */
  closeFromButton = () => {
    this.#allowClose = true;
    // Mark the close time so the cart drawer suppresses any in-flight add-to-cart
    // event that resolves just after the modal closes (otherwise the drawer pops open).
    window.__qmzQuickAddClosedAt = Date.now();
    this.closeDialog();
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    // Intentionally do NOT close the modal here so clients can add multiple
    // sizes/quantities in one session. Closing happens only via "Shop More".
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
